import React, { useEffect } from 'react';
import { Drawer, Form, Button, SelectPicker, Message, CheckPicker } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { getServicelines } from '@/services/serviceline.service';
import { useTranslation } from 'react-i18next';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    department: string;
    modes: string[];
  };
  users: User[];
  usersLoading: boolean;
  toaster: any;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  usersLoading,
  toaster
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: servicelines, loading: servicelineLoading } = useSelector(
    state => state.serviceLine
  );
  const [formValue, setFormValue] = React.useState({
    ...currentFilters,
    modes: currentFilters.modes || []
  });

  const userOptions = React.useMemo(() => {
    return users?.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  const modesOptions = React.useMemo(
    () => [
      { label: t('Home office'), value: 'WFH' },
      { label: t('On Site With CheckIn'), value: 'OnSiteWithCheckin' },
      { label: t('On Site Without CheckIn'), value: 'OnSiteWithoutCheckin' },
      { label: t('Third Party Site'), value: 'ThirdPartySite' },
      { label: t('Leave'), value: 'Leave' },
      { label: t('Business Travel'), value: 'TRAVEL' },
      { label: t('Untraced'), value: 'Untraced' }
    ],
    [t]
  );

  useEffect(() => {
    dispatch(getServicelines());
  }, [dispatch]);

  const handleSubmit = () => {
    console.log('Form value on submit:', formValue);
    const hasFilters = Object.values(formValue).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    });

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId || '',
        department: formValue.department || '',
        modes: formValue.modes || []
      });
      toaster.push(
        <Message type="success" closable>
          {t('Filters applied successfully')}
        </Message>
      );
    } else {
      onResetFilters();
    }

    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      department: '',
      modes: []
    });
    onResetFilters();
    toaster.push(
      <Message type="info" closable>
        Filters have been reset
      </Message>
    );
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue({ ...currentFilters, modes: currentFilters.modes || [] });
    }
  }, [open, currentFilters]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Team')}</Form.ControlLabel>
            <Form.Control
              name="department"
              accepter={SelectPicker}
              data={
                servicelines?.map(sl => ({
                  label: sl.serviceLine,
                  value: sl.serviceLine
                })) || []
              }
              loading={servicelineLoading}
              searchable
              placeholder={t('Select Team')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Modes')}</Form.ControlLabel>
            <Form.Control
              name="modes"
              accepter={CheckPicker}
              data={modesOptions}
              searchable
              placeholder={t('Select modes')}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
